<template>
  <div class="blockPostAddress mt-8" @click="editScenario">
    <h3 class="text-lg font-medium pb-[6px] lg1024:text-2xl">
      {{ $t('enter_destination_address') }}
    </h3>

    <span
      class="font-normal text-base leading-6 tracking-[-0.014em] text-[#75828B]"
    >
      {{ $t('post_address_subtitle') }}
    </span>

    <template v-if="!hideGmapAutocomplete">
      <div
        v-show="showHiddenFields"
        class="font-medium cursor-pointer flex mt-2 gap-1 w-min whitespace-nowrap text-[#E03B00]"
        @click="emit('hideHiddenFields')"
        data-qa="step4-post-hide-hidden-btn-top"
      >
        <img
          src="@/assets/img/close-map.svg"
          alt=""
          class="w-[24px] h-[24px]"
        />
        <span>{{ $t('checkout_hide_manual_address') }}</span>
      </div>
    </template>

    <div class="flex flex-col mt-3 w-full">
      <div
        v-if="!hideGmapAutocomplete && !showHiddenFields"
        :class="[
          'autocomplete-form',
          {
            'autocomplete-form-error': courierAddressFieldError,
          },
        ]"
      >
        <label class="text-lighter" for="post-address-field">
          {{ getAutocompleteLabel }}<span class="required">*</span>
        </label>
        <GMapAutocomplete
          id="post-address-field"
          class="border border-gray-2 p-3 w-full mt-1 input-wrap lg1024:w-4/4"
          data-qa="step4-courier-autocomplete"
          :placeholder="$t('shipping_address_mask')"
          :bounds="positionData.circle"
          :strict-bounds="true"
          :component-restrictions="{ country: countryRestriction }"
          :value="addressInfo.formatted"
          ref="post-address-field"
          @place_changed="formAddressField"
          @focusout="validateAddressField($event.target.value, true)"
          @keyup="formAddressField(false, $event)"
          @input="incrementAddressEditCounter(getCheckoutContext(store))"
          :fields="[
            'address_components',
            'formatted_address',
            'geometry',
            'place_id',
          ]"
        />
        <p
          v-if="courierAddressFieldError"
          class="mb-2 text-sm text-accentColor"
        >
          {{ courierAddressFieldError }}.&nbsp;
        </p>
      </div>

      <div
        class="mt-2 w-full grid grid-cols-2 gap-4 lg1024:w-4/4 lg1024:grid-cols-4 max-lg:grid-cols-1"
      >
        <template v-for="field in getAdditionalFields" :key="field.name">
          <div
            v-if="
              (field.type === 'text' || field.type === 'number') &&
              getFieldPropertyValue('ADDRESS_INFO_FIELDS', field.name, 'label')
            "
            v-show="
              field.visual ||
              (showHiddenFields &&
                !['FIO', 'EMAIL', 'PHONE', 'COUNTRY', 'CITY', 'AREA'].includes(
                  field.name
                ))
            "
            :class="{
              'lg1024:col-span-4 col-span-2 order-first':
                (hideGmapAutocomplete || showHiddenFields) &&
                field.name.toLowerCase() === 'street',
              'lg1024:col-span-4 col-span-2':
                field.name.toLowerCase() === 'apartment' && englishAddress,
            }"
            class="flex flex-col justify-start"
          >
            <label :for="field.name.toLowerCase()" class="flex">
              {{
                getFieldPropertyValue(
                  'ADDRESS_INFO_FIELDS',
                  field.name,
                  'label'
                )
              }}
              <span
                class="required"
                v-if="
                  getFieldPropertyValue(
                    'ADDRESS_INFO_FIELDS',
                    field.name,
                    'required'
                  )
                "
              >
                *
              </span>
            </label>
            <input
              class="border border-gray-2 p-3 input-wrap mt-1"
              autocomplete="off"
              :id="field.name.toLowerCase()"
              :data-qa="'step4-post-input-' + field.name.toLowerCase()"
              :minlength="field.min"
              :maxlength="field.max ? field.max + 1 : null"
              :type="field.type === 'number' ? 'text' : field.type"
              :inputmode="field.type === 'number' ? 'numeric' : null"
              @input="handleMaxLength($event, field)"
              @focusout="handleFieldFocusOut(field, $event.target.value)"
              @change="emit('manualAddressChange', field.name)"
              @keyup="
                validateField(
                  'ADDRESS_INFO_FIELDS',
                  field.name,
                  $event.target.value
                )
              "
              v-model="addressInfo.PROFILE[field.name]"
              :class="{
                'has-error': addressFieldErrors[field.name]?.length,
              }"
            />
            <p
              v-if="errorMessages[field.name]"
              class="text-orange-500 text-xs leading-none mt-1"
            >
              {{ errorMessages[field.name] }}
            </p>
          </div>
        </template>
      </div>

      <div class="mt-2 w-full grid lg1024:w-3/4">
        <div class="flex flex-col justify-start" v-if="getCountryCode === 'DE'">
          <label class="text-lighter" for="profileAddressComment">
            {{ $t('address_comment') }}
          </label>
          <input
            id="profileAddressComment"
            class="border border-gray-2 p-3 mt-1 input-wrap"
            type="text"
            data-qa="step4-post-address-comment"
            v-model="profileAddressComment"
            autocomplete="off"
          />
        </div>
      </div>

      <div
        class="my-2 flex flex-col"
        v-if="!showHiddenFields && !hideGmapAutocomplete"
      >
        <span class="text-[#333F48] text-[16px]" id="showHiddenFields">
          {{ $t('show_hidden_fields') }}
        </span>
        <div
          class="flex gap-1 mt-4 cursor-pointer w-min whitespace-nowrap"
          @click="emit('showHiddenFields')"
          data-qa="step4-post-show-hidden-btn"
        >
          <img src="@/assets/img/edit-icon.svg" alt="" />
          <p class="text-[16px] text-[#E03B00] font-medium">
            {{ $t('show_hidden_fields_button') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useAddressFields } from '../composables/useAddressFields'
import {
  getCheckoutContext,
  incrementAddressEditCounter,
} from '@/utils/sentryEvents'

const props = defineProps({
  modelValue: { type: Object, required: true }, // это addressInfo
  positionData: Object, // геоданные, включая круг ограничений для autocomplete
  countryRestriction: [String, Array], // код страны для ограничения подсказок autocomplete
  getCountryCode: String, // двухсимвольный код страны (например, 'DE', 'RU')
  getFieldPropertyValue: Function, // получить значение свойства поля (label, required, type)
  validateField: Function, // валидация одного поля при фокусе/вводе
  onAddressFieldFocusOut: Function, // уход с поля адреса: дозапрос индекса
  handleMaxLength: Function, // обрезка значения при maxlength
  formAddressField: Function, // обработка выбора адреса из карты/поиска
  validateAddressField: Function, // отдельная валидация поля адреса
  errorMessages: Object, // дополнительные ошибки по полям
  fieldsConfig: Object, // конфиг полей адреса
  getAdditionalFields: Array, // массив объектов с описанием полей (name, type, min, max и т.д.)
  getAutocompleteLabel: String, // текст лейбла autocomplete
  englishAddress: Boolean, // признак англоязычного адреса (для отображения лейблов)
  showHiddenFields: Boolean, // показываются ли дополнительные поля (ручной ввод)
  hideGmapAutocomplete: Boolean, // скрывать ли autocomplete вообще
})

const emit = defineEmits([
  'update:modelValue',
  'update:profileAddressComment',

  'showHiddenFields', // при нажатии "Показать скрытые поля"
  'hideHiddenFields', // при нажатии "Скрыть скрытые поля"
  'manualAddressChange',
])
// приоритет: вручную введённые данные → профиль по умолчанию
const { addressInfo } = useAddressFields(props, emit, 'post_address')
// любая правка addressInfo.* (даже глубоко внутри PROFILE) будет ловиться watch
watch(
  addressInfo,
  (val) => emit('update:modelValue', val),
  { deep: true } // важно!
)

const store = useStore()
const courierAddressFieldError = computed(
  () => store.getters.courierAddressFieldError
)
const addressFieldErrors = computed(() => store.getters.addressFieldErrors)

function handleFieldFocusOut(field, value) {
  props.validateField('ADDRESS_INFO_FIELDS', field.name, value)
  props.onAddressFieldFocusOut?.(field.name)
}

function editScenario() {
  store.commit('setCheckoutScenario', 'userEditProfile')
}
</script>
